"use client"

import { useEffect, useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { Loader2, ImageOff, ArrowDownLeft, ArrowUpRight, ArrowLeftRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { fetchSlipDetail } from "@/src/lib/api/slip-api"
import { SlipDetail } from "@/src/types/slip"

interface SlipDetailDialogProps {
  /** The transaction id to load, or null when the dialog is closed */
  transactionId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDateTime(value: string | null): string {
  if (!value) return "-"
  const d = new Date(value)
  if (isNaN(d.getTime())) return value
  return d.toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" })
}

function formatAmount(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(Number(value))) return "-"
  return Number(value).toLocaleString("th-TH", { minimumFractionDigits: 2 })
}

function TypeBadge({ type }: { type: string | null }) {
  const normalized = (type || "").toUpperCase()
  if (normalized === "INCOME") {
    return (
      <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100 gap-1">
        <ArrowDownLeft className="h-3 w-3" /> รายรับ
      </Badge>
    )
  }
  if (normalized === "EXPENSE") {
    return (
      <Badge className="bg-red-100 text-red-700 border-red-200 hover:bg-red-100 gap-1">
        <ArrowUpRight className="h-3 w-3" /> รายจ่าย
      </Badge>
    )
  }
  if (normalized === "TRANSFER") {
    return (
      <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100 gap-1">
        <ArrowLeftRight className="h-3 w-3" /> โอน
      </Badge>
    )
  }
  return <span className="text-sm text-gray-400">-</span>
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 text-sm">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-gray-900 text-right break-words max-w-[65%]">{value ?? "-"}</span>
    </div>
  )
}

// Resolve a possibly-relative image path to something the browser can load.
// Mirrors the axios baseURL logic: when VITE_API_BASE_URL is set the Vite proxy
// serves relative paths; otherwise fall back to VITE_API_URL.
function resolveImageSrc(url: string): string {
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url
  const env = import.meta.env as Record<string, string | undefined>
  const base = (env.VITE_API_BASE_URL ? "" : (env.VITE_API_URL || "")).replace(/\/$/, "")
  const path = url.startsWith("/") ? url : `/${url}`
  return base ? `${base}${path}` : path
}

// ─── Component ───────────────────────────────────────────────────────────────

export function SlipDetailDialog({ transactionId, open, onOpenChange }: SlipDetailDialogProps) {
  const [detail, setDetail] = useState<SlipDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [imageError, setImageError] = useState(false)

  useEffect(() => {
    if (!open || !transactionId) return

    let cancelled = false
    setLoading(true)
    setError(null)
    setDetail(null)
    setImageError(false)

    fetchSlipDetail(transactionId)
      .then((res) => {
        if (cancelled) return
        setDetail(res.data.transaction)
      })
      .catch((err) => {
        if (cancelled) return
        setError(
          err?.response?.data?.error?.message ||
            err?.message ||
            "ไม่สามารถโหลดรายละเอียดสลิปได้"
        )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, transactionId])

  const imageSrc = detail?.slip_image_url ? resolveImageSrc(detail.slip_image_url) : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>รายละเอียดสลิป</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {!loading && !error && detail && (
          <div className="max-h-[70vh] overflow-y-auto space-y-4">
            {/* Uploaded slip image */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">ภาพสลิปที่อัพโหลด</p>
              {imageSrc && !imageError ? (
                <a href={imageSrc} target="_blank" rel="noopener noreferrer">
                  <img
                    src={imageSrc}
                    alt="สลิปที่อัพโหลด"
                    className="w-full max-h-80 object-contain rounded-lg border bg-gray-50"
                    onError={() => setImageError(true)}
                  />
                </a>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 py-8 rounded-lg border border-dashed bg-gray-50 text-gray-400">
                  <ImageOff className="h-8 w-8" />
                  <p className="text-sm">ไม่มีภาพสลิปสำหรับรายการนี้</p>
                </div>
              )}
            </div>

            {/* Transaction details */}
            <div className="rounded-lg border divide-y">
              <div className="px-4 py-2 flex items-center justify-between bg-gray-50">
                <span className="text-sm font-medium text-gray-700">ข้อมูลรายการ</span>
                <TypeBadge type={detail.transaction_type} />
              </div>
              <div className="px-4 py-2">
                <DetailRow label="รหัสอ้างอิง" value={
                  detail.transaction_ref
                    ? <span className="font-mono text-xs break-all">{detail.transaction_ref}</span>
                    : "-"
                } />
                <DetailRow
                  label="จำนวนเงิน"
                  value={<span className="font-bold text-gray-900">{formatAmount(detail.amount)} บาท</span>}
                />
                <DetailRow label="วันเวลาโอน" value={formatDateTime(detail.transaction_date)} />
                <DetailRow label="ธนาคาร" value={detail.bank_code || "-"} />
              </div>
            </div>

            {/* Sender / Receiver */}
            <div className="rounded-lg border divide-y">
              <div className="px-4 py-2 bg-gray-50">
                <span className="text-sm font-medium text-gray-700">ผู้โอน / ผู้รับ</span>
              </div>
              <div className="px-4 py-2">
                <DetailRow
                  label="ผู้โอน"
                  value={
                    <span>
                      {detail.sender.account_name || "-"}
                      {detail.sender.account_number && (
                        <span className="block text-xs text-gray-400">{detail.sender.account_number}</span>
                      )}
                    </span>
                  }
                />
                <DetailRow
                  label="ผู้รับ"
                  value={
                    <span>
                      {detail.receiver.account_name || "-"}
                      {detail.receiver.account_number && (
                        <span className="block text-xs text-gray-400">{detail.receiver.account_number}</span>
                      )}
                    </span>
                  }
                />
              </div>
            </div>

            {/* Status */}
            <div className="rounded-lg border divide-y">
              <div className="px-4 py-2 bg-gray-50">
                <span className="text-sm font-medium text-gray-700">สถานะการตรวจสอบ</span>
              </div>
              <div className="px-4 py-2">
                <DetailRow
                  label="สถานะ"
                  value={
                    <span
                      className={cn(
                        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                        (detail.verification_status || "").toLowerCase() === "verified"
                          ? "bg-green-100 text-green-800"
                          : (detail.verification_status || "").toLowerCase() === "failed"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-600"
                      )}
                    >
                      {detail.verification_status || "-"}
                    </span>
                  }
                />
                {detail.is_duplicate && <DetailRow label="รายการซ้ำ" value="ใช่" />}
                {detail.error_message && (
                  <DetailRow
                    label="ข้อผิดพลาด"
                    value={<span className="text-red-600 text-xs">{detail.error_message}</span>}
                  />
                )}
                <DetailRow label="บันทึกเมื่อ" value={formatDateTime(detail.created_at)} />
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
